import React from "react";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../app/hooks";
import { selectIsOrgOwnerOrAdmin } from "../../features/authentication/authenticationSlice";
import { MemberBillingIssues } from "../../features/billing/models/MemberBillingIssues";

interface MemberBillingChipProps {
  issues?: MemberBillingIssues;
  /** Short label of the member's active contract ("Visit pack", "Flat subscription", …). */
  contractLabel?: string;
}

/**
 * Compact billing standing for member lists: the active contract (or its absence)
 * plus a red overdue marker when payments are outstanding.
 * Billing is an Owner/Admin concern — plain Instructors never see it.
 */
const MemberBillingChip: React.FC<MemberBillingChipProps> = ({ issues, contractLabel }) => {
  const { t } = useTranslation();
  const isOrgOwnerOrAdmin = useAppSelector(selectIsOrgOwnerOrAdmin);
  if (!isOrgOwnerOrAdmin) return null;
  if (!issues) return null;

  return (
    <span className="inline-flex flex-wrap items-center gap-1">
      <span className="inline-flex items-center rounded-full bg-grey-95 px-2 py-0.5 text-xs font-medium text-grey-30 dark:bg-primary-20 dark:text-grey-70">
        {issues.hasActiveContract
          ? (contractLabel ?? t("billing.chip.contract"))
          : t("billing.chip.noContract")}
      </span>
      {issues.overduePaymentsCount > 0 && (
        <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-800 dark:bg-red-900/30 dark:text-red-300">
          {t("billing.chip.overdue", { count: issues.overduePaymentsCount })}
        </span>
      )}
      {issues.draftPaymentsCount > 0 && (
        <span className="inline-flex items-center rounded-full bg-secondary-95 px-2 py-0.5 text-xs font-medium text-secondary-30 dark:bg-secondary-20 dark:text-secondary-80">
          {t("billing.chip.drafts", { count: issues.draftPaymentsCount })}
        </span>
      )}
    </span>
  );
};

export default MemberBillingChip;
